import { isDateOnly, parseDateOnly } from '@/lib/dates';

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Partial<Record<string, string | boolean | number>> = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === false || v == null) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'html') node.innerHTML = String(v);
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Escape user content for safe interpolation into innerHTML. */
export function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function fmtMoney(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  });
}

// The tab title's suffix: the product's until a firm is known (main.ts sets
// the signed-in firm's name; portals set the sending firm's). Never a built-in
// firm name — see host-branding.ts.
let titleBase = 'Design Studio HQ';

export function setTitleBase(base: string): void {
  const b = base.trim();
  if (b) titleBase = b;
}
/**
 * `base` overrides the brand suffix — the sign-in page passes the product name
 * when it's served on the product domain (see @/lib/host-branding).
 */
export function setPageTitle(part?: string | null, base: string = titleBase): void {
  document.title = part ? `${part} · ${base}` : base;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Date-only strings ("YYYY-MM-DD", what Postgres `date` columns return) must
  // render as the literal calendar day — see the note in @/lib/dates for why
  // `new Date(iso)` is wrong here.
  if (isDateOnly(iso)) {
    const d = parseDateOnly(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }
  // Full timestamps (timestamptz) still localize to the viewer's timezone.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
